// Garde d'authentification (ADR 0023) : charge GET /me et fournit l'utilisateur (droits, préférences) à l'application.
// Sans session (401) : redirection vers la connexion (ACC-P01) avec retour à la page demandée.
import { Navigate, useRouterState } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useV1MeShow } from '@/api/generated/endpoints/me/me';
import { TableDensityContext } from '@/design-system/table-density';
import { ErrorState, LoadingState } from '@/design-system/States';
import { isApiError } from '@/lib/errors';
import { LOGIN_PATH, PUBLIC_PATHS, safeRedirect, SECURITY_PATH } from './auth';
import { applyLocale } from './i18n';
import { MeProvider } from './me';
import { useTheme, type ThemePreference } from './theme';

export function AuthGuard({ children }: { children: ReactNode }) {
  const query = useV1MeShow({ query: { staleTime: 5 * 60_000 } });
  const { setPreference } = useTheme();
  const href = useRouterState({ select: (state) => state.location.href });
  const me = query.data?.data;

  // Le thème enregistré sur le compte (SYS-P04) l'emporte sur le choix local de l'appareil.
  useEffect(() => {
    const theme = me?.preferences.theme;
    if (theme === 'light' || theme === 'dark' || theme === 'system') setPreference(theme as ThemePreference);
  }, [me?.preferences.theme, setPreference]);

  // Langue du compte (SYS-P04, TRV-08 ; lot F17d) : français, ou mooré / dioula une fois proposés par l'API.
  useEffect(() => {
    if (me) applyLocale(me.preferences.locale);
  }, [me]);

  if (query.isPending) {
    return (
      <div className="mx-auto max-w-5xl p-8">
        <LoadingState />
      </div>
    );
  }

  if (query.error || !me) {
    const error = query.error;
    if (isApiError(error) && error.kind === 'unauthenticated') {
      // Pendant la transition, la garde reste montée sur l'URL de connexion : ne pas relancer la redirection (le
      // paramètre de retour serait perdu).
      if (PUBLIC_PATHS.some((path) => href.startsWith(path))) return null;
      const redirect = safeRedirect(href);
      return <Navigate to={LOGIN_PATH} search={redirect === '/' ? {} : { redirect }} replace />;
    }
    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg p-4">
        <ErrorState message={isApiError(error) ? error.message : undefined} reference={isApiError(error) ? error.reference : undefined} onRetry={() => void query.refetch()} />
      </div>
    );
  }

  // ACC-14 : profil sensible sans vérification en deux étapes — sa configuration (SYS-P03) avant tout autre écran.
  if (me.two_factor_setup_required && !href.startsWith(SECURITY_PATH)) {
    return <Navigate to={SECURITY_PATH} replace />;
  }

  return (
    <MeProvider me={me}>
      <TableDensityContext.Provider value={me.preferences.table_density === 'dense' ? 'dense' : 'comfortable'}>{children}</TableDensityContext.Provider>
    </MeProvider>
  );
}
